import { z } from "zod"
import {
  clearContactModalSearch,
  contactModalSearchSchema,
  contactModalViewSchema,
} from "@/src/shared/contacts/searchSchemas"
import {
  clearInviteModalSearch,
  inviteModalSearchSchema,
  inviteModalViewSchema,
} from "@/src/shared/invites/searchSchemas"
import {
  clearProjectRecordEmailModalSearch,
  projectRecordEmailModalSearchSchema,
} from "@/src/shared/projectRecordEmails/searchSchemas"
import {
  clearProjectRecordModalSearch,
  projectRecordModalSearchSchema,
  projectRecordModalViewSchema,
} from "@/src/shared/projectRecords/searchSchemas"
import {
  clearProjectUploadModalSearch,
  projectUploadModalSearchSchema,
  projectUploadModalViewSchema,
} from "@/src/shared/uploads/searchSchemas"

export function clearAllProjectModalSearch<TSearch extends Record<string, unknown>>(
  search: TSearch,
) {
  return clearInviteModalSearch(
    clearContactModalSearch(
      clearProjectRecordEmailModalSearch(
        clearProjectRecordModalSearch(clearProjectUploadModalSearch(search)),
      ),
    ),
  )
}

export const loggedInProjectModalSearchSchema = z
  .object({
    modalUploadId: z.coerce.number().int().positive().optional().catch(undefined),
    modalUploadView: projectUploadModalViewSchema.optional().catch(undefined),
    modalProjectRecordId: z.coerce.number().int().positive().optional().catch(undefined),
    modalProjectRecordView: projectRecordModalViewSchema.optional().catch(undefined),
    modalProjectSlug: z.string().optional().catch(undefined),
    modalProjectRecordEmailId: z.coerce.number().int().positive().optional().catch(undefined),
    modalContactId: z.coerce.number().int().positive().optional().catch(undefined),
    modalContactView: contactModalViewSchema.optional().catch(undefined),
    modalInviteView: inviteModalViewSchema.optional().catch(undefined),
  })
  .transform((search) => {
    const uploadSearch = projectUploadModalSearchSchema.parse(search)
    const projectRecordSearch = projectRecordModalSearchSchema.parse(search)
    const projectRecordEmailSearch = projectRecordEmailModalSearchSchema.parse(search)
    const contactSearch = contactModalSearchSchema.parse(search)
    const inviteSearch = inviteModalSearchSchema.parse(search)
    const hasUploadModal =
      uploadSearch.modalUploadId !== undefined && uploadSearch.modalUploadView !== undefined
    const hasProjectRecordModal =
      projectRecordSearch.modalProjectRecordId !== undefined &&
      projectRecordSearch.modalProjectRecordView !== undefined
    const hasProjectRecordEmailModal =
      projectRecordEmailSearch.modalProjectRecordEmailId !== undefined
    const hasContactModal = contactSearch.modalContactView !== undefined
    const hasInviteModal = inviteSearch.modalInviteView !== undefined

    const activeModalCount = [
      hasUploadModal,
      hasProjectRecordModal,
      hasProjectRecordEmailModal,
      hasContactModal,
      hasInviteModal,
    ].filter(Boolean).length

    if (activeModalCount > 1) {
      return clearAllProjectModalSearch(search)
    }

    return {
      ...uploadSearch,
      ...projectRecordSearch,
      ...projectRecordEmailSearch,
      ...contactSearch,
      ...inviteSearch,
    }
  })

export type LoggedInProjectModalSearch = z.infer<typeof loggedInProjectModalSearchSchema>

const NO_PROJECT_MODAL_SEARCH = loggedInProjectModalSearchSchema.parse({})

/** A search read across routes comes back as unvalidated strings, so the hosts parse it here. */
export function parseProjectModalSearch(search: unknown): LoggedInProjectModalSearch {
  const parsed = loggedInProjectModalSearchSchema.safeParse(search ?? {})

  return parsed.success ? parsed.data : NO_PROJECT_MODAL_SEARCH
}
